import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";
import { useAuth, canAtLeast } from "../lib/auth";
import { Plus, LayoutDashboard, Trash2 } from "lucide-react";

export default function DashboardsList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/dashboards").then((r) => setItems(r.data.dashboards || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Delete this dashboard?")) return;
    try {
      await api.delete(`/dashboards/${id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content">
        <div className="erp-page-title-row">
          <div>
            <h1>Dashboards</h1>
            <p>Saved analytics dashboards for this workspace. Create, edit, and manage analytics dashboards from here.</p>
          </div>

          {canAtLeast(user, "analyst") && (
            <button className="erp-primary-btn" onClick={() => nav("/builder/new")} data-testid="dashboards-new">
              <Plus size={15} strokeWidth={1.8} />
              New dashboard
            </button>
          )}
        </div>

        <div className="erp-card">
          {loading ? (
            <div className="text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>
              Loading…
            </div>
          ) : (
            <table className="tbl" data-testid="dashboards-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Tags</th>
                  <th>Type</th>
                  <th>Updated</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {items.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <Link
                        to={`/dashboards/${d.id}`}
                        className="font-medium"
                        style={{ color: "var(--c-accent)" }}
                        data-testid={`dash-row-${d.id}`}
                      >
                        <LayoutDashboard size={14} strokeWidth={1.7} className="inline mr-1.5" />
                        {d.name}
                      </Link>
                      <div className="text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>
                        {d.description || "—"}
                      </div>
                    </td>

                    <td>
                      <div className="flex gap-1 flex-wrap">
                        {(d.tags || []).map((t) => <span className="pill" key={t}>{t}</span>)}
                      </div>
                    </td>

                    <td>
                      {d.is_template ? <span className="pill info">Template</span> : <span className="pill">Custom</span>}
                    </td>

                    <td className="numeric text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>
                      {new Date(d.updated_at).toLocaleString()}
                    </td>

                    <td className="text-right">
                      {canAtLeast(user, "analyst") && (
                        <button className="btn btn-ghost" onClick={() => nav(`/builder/${d.id}`)} data-testid={`edit-${d.id}`}>
                          Edit
                        </button>
                      )}

                      {canAtLeast(user, "workspace_admin") && !d.is_template && (
                        <button className="btn btn-ghost" onClick={() => del(d.id)} data-testid={`del-${d.id}`}>
                          <Trash2 size={13} strokeWidth={1.7} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                {items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-6" style={{ color: "var(--c-text-muted)" }}>
                      No dashboards yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
